import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { jwtDecode } from 'jwt-decode';
import DownloadOutlined from '@mui/icons-material/DownloadOutlined';
import EditOutlined from '@mui/icons-material/EditOutlined';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import PictureAsPdfOutlined from '@mui/icons-material/PictureAsPdfOutlined';
import api from '../../services/api';
import { AuthContext } from '../../contexts/AuthContext';
import { buildMonthlySummaryDoc, exportMonthlySummaryPdf } from '../qdf/services/monthlySummaryPdf';
import AppShell from '../../components/layout/AppShell';
import {
  Button,
  PageHeader,
  PageLoader,
  Alert,
  Badge,
  EmptyState,
  ConfirmDialog,
} from '../../components/ui';

const ViewMonthlyReport = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token: authToken, role } = useContext(AuthContext);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingTooLong, setLoadingTooLong] = useState(false);
  const [error, setError] = useState('');
  const [previewUrls, setPreviewUrls] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Determine if the current user is an admin
  let isAdmin = false;
  if (authToken) {
    try {
      const decoded = jwtDecode(authToken);
      isAdmin = decoded.role === 'admin';
    } catch (err) {
      console.error('Error decoding token:', err);
    }
  }

  useEffect(() => {
    const fetchReport = async () => {
      try {
        // Use authToken from the context instead of reading from localStorage again.
        const response = await api.get(`/monthly-report/monthly-reports/${id}`, {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });
        setReport(response.data);
        setLoading(false);
      } catch (error) {
        setError('Failed to fetch report. Please try again later.');
        setLoading(false);
        console.error('Error fetching report:', error);
      }
    };

    fetchReport();
  }, [id, authToken]);

  // Fetch PDF blobs for preview once report is loaded
  useEffect(() => {
    let createdUrls = [];
    const fetchPreviews = async () => {
      if (!report) return;

      // Generated QDF summary reports have no uploaded PDF; build one from the stored summary.
      if (report.reportMode === 'generated' || report.summary) {
        try {
          const doc = buildMonthlySummaryDoc(report.summary);
          if (doc) {
            const url = doc.output('bloburl').toString();
            createdUrls = [url];
            setPreviewUrls([url]);
          }
        } catch (e) {
          console.error('Failed to build summary preview', e);
        }
        return;
      }

      if (!report.pdfFile) return;
      try {
        const urls = [];
        for (const filePath of report.pdfFile) {
          const resp = await api.get(`/${filePath}`, {
            headers: { Authorization: `Bearer ${authToken}` },
            responseType: 'blob',
          });
          const url = URL.createObjectURL(resp.data);
          urls.push(url);
        }
        createdUrls = urls;
        setPreviewUrls(urls);
      } catch (e) {
        console.error('Failed to load previews', e);
      }
    };
    fetchPreviews();
    return () => {
      createdUrls.forEach(u => URL.revokeObjectURL(u));
    };
  }, [report, authToken]);

  // Timeout to show logout suggestion if loading takes too long
  useEffect(() => {
    let timeoutId;
    if (loading) {
      timeoutId = setTimeout(() => {
        setLoadingTooLong(true);
      }, 10000); // Show message after 10 seconds
    } else {
      setLoadingTooLong(false);
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [loading]);

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('userId');
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('userId');
    navigate('/login');
  };

  const handleDownload = async () => {
    try {
      const zip = new JSZip();
      const folder = zip.folder('MonthlyReportFiles');

      for (const filePath of report.pdfFile) {
        const response = await api.get(`/${filePath}`, {
          headers: { Authorization: `Bearer ${authToken}` },
          responseType: 'blob',
        });

        const fileName = filePath.split('/').pop();
        folder.file(fileName, response.data);
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      saveAs(zipBlob, `MonthlyReportFiles-${report.clientName}.zip`);
    } catch (error) {
      console.error('Error downloading files:', error);
      setError('Failed to download the files. Please try again later.');
    }
  };

  const handleUpdate = () => {
    // This should only be available for admin users
    navigate('/add-monthly-report', { state: { report } });
  };

  const handleDelete = async () => {
    if (!authToken) {
      toast.error('You are not logged in. Please log in and try again.');
      return;
    }

    let tokenRole;
    try {
      ({ role: tokenRole } = jwtDecode(authToken));
    } catch (decodeError) {
      console.error('Error checking user role:', decodeError);
      toast.error('Could not verify your permissions. Please log in again.');
      return;
    }

    if (tokenRole !== 'admin') {
      toast.error('You do not have permission to delete this report.');
      return;
    }

    setDeleting(true);
    try {
      await api.delete(`/monthly-report/monthly-reports/${id}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      toast.success('Report deleted.');
      navigate('/viewReports');
    } catch (deleteError) {
      console.error('Error deleting report:', deleteError);
      setError('Failed to delete the report. Please try again later.');
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  if (role !== 'admin') {
    return (
      <AppShell>
        <Alert
          variant="error"
          title="Access denied"
          action={
            <Button variant="secondary" size="sm" onClick={() => navigate('/')}>
              Back to home
            </Button>
          }
        >
          You do not have permission to access monthly reports. Please contact an administrator.
        </Alert>
      </AppShell>
    );
  }

  if (loading) {
    return (
      <AppShell>
        <PageLoader label="Loading monthly report…" />
        {loadingTooLong && (
          <Alert
            variant="warning"
            title="Taking longer than usual"
            className="mx-auto max-w-md"
            action={
              <Button variant="secondary" size="sm" onClick={handleLogout}>
                Log out and retry
              </Button>
            }
          >
            Your session may have expired.
          </Alert>
        )}
      </AppShell>
    );
  }

  if (!report) {
    return (
      <AppShell>
        <Alert
          variant="error"
          title="Report not found"
          action={
            <Button variant="secondary" size="sm" onClick={() => navigate('/viewReports')}>
              Back to reports
            </Button>
          }
        >
          {error || 'This report may have been deleted.'}
        </Alert>
      </AppShell>
    );
  }

  const isGenerated = report.reportMode === 'generated';
  const hasFiles = Array.isArray(report.pdfFile) && report.pdfFile.length > 0;

  return (
    <>
      <PageHeader
        title="Monthly report"
        subtitle={`${report.clientName} · ${new Date(report.date).toLocaleDateString('en-GB')}`}
        backTo="/viewReports"
        actions={
          <>
            {isGenerated
              ? report.summary && (
                  <Button
                    variant="secondary"
                    icon={DownloadOutlined}
                    onClick={() => exportMonthlySummaryPdf(report.summary)}
                  >
                    Download PDF
                  </Button>
                )
              : hasFiles && (
                  <Button variant="secondary" icon={DownloadOutlined} onClick={handleDownload}>
                    Download all
                  </Button>
                )}

            {isAdmin && (
              <>
                {!isGenerated && (
                  <Button icon={EditOutlined} onClick={handleUpdate}>
                    Update
                  </Button>
                )}
                <Button
                  variant="danger-soft"
                  icon={DeleteOutline}
                  onClick={() => setConfirmDelete(true)}
                >
                  Delete
                </Button>
              </>
            )}
          </>
        }
      />

      <AppShell>
        {error && (
          <Alert variant="error" className="mb-5">
            {error}
          </Alert>
        )}

        <section className="mb-6 rounded-lg border border-border bg-surface p-4 shadow-sm sm:p-5">
          <dl className="grid gap-4 sm:grid-cols-3">
            <div>
              <dt className="text-xs uppercase tracking-wide text-muted-fg">Client</dt>
              <dd className="mt-0.5 text-sm text-foreground">{report.clientName || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-muted-fg">Date</dt>
              <dd className="mt-0.5 text-sm text-foreground">
                {new Date(report.date).toLocaleDateString('en-GB')}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-muted-fg">Source</dt>
              <dd className="mt-0.5">
                <Badge variant="neutral" size="sm">
                  {isGenerated ? 'Generated summary' : 'Uploaded files'}
                </Badge>
              </dd>
            </div>
          </dl>
        </section>

        <section>
          <h2 className="mb-3 text-sm font-semibold text-foreground">
            {isGenerated ? 'Report preview' : 'PDF previews'}
          </h2>

          {previewUrls && previewUrls.length > 0 ? (
            <div className="space-y-4">
              {previewUrls.map((url, index) => (
                <div key={index} className="overflow-hidden rounded-lg border border-border bg-surface shadow-sm">
                  {!isGenerated && (
                    <div className="border-b border-border px-4 py-2 text-sm font-medium text-muted-fg">
                      File {index + 1}
                    </div>
                  )}
                  {/* Shorter on phones — a 500px frame leaves no room for anything else. */}
                  <iframe
                    src={url}
                    title={`PDF preview ${index + 1}`}
                    className="h-[60vh] w-full border-0 sm:h-[38rem]"
                  />
                </div>
              ))}
            </div>
          ) : (
            <EmptyState
              icon={PictureAsPdfOutlined}
              title="No preview available"
              description="There is no document attached to this report."
            />
          )}
        </section>
      </AppShell>

      <ConfirmDialog
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
        loading={deleting}
        title="Delete monthly report"
        message="This permanently removes the report and its attached files. This cannot be undone."
        confirmLabel="Delete"
      />
    </>
  );
};

export default ViewMonthlyReport;
